import { useEffect, useState } from "react";

const DEFAULT_DURATION_MS = 1100;

interface UseAnimatedCountOptions {
  targetValue: number;
  duration?: number;
  isDisabled?: boolean;
  onComplete?: () => void;
}

/**
 * Hook to animate a value from 0 to `targetValue` over `duration` milliseconds.
 */
export const useAnimatedCount = ({
  targetValue,
  duration = DEFAULT_DURATION_MS,
  isDisabled = false,
  onComplete,
}: UseAnimatedCountOptions): number => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isDisabled) {
      setDisplayValue(targetValue);
      return undefined;
    }

    let frameId = 0;
    const startAt = performance.now();
    const safeDuration = duration > 0 ? duration : 1;

    const tick = (now: number) => {
      const elapsed = now - startAt;
      const t = Math.min(1, elapsed / safeDuration);
      const eased = easeOutCubic(t);
      const raw = targetValue * eased;
      setDisplayValue(formatAnimatedStep(raw, targetValue));

      if (t < 1) {
        frameId = window.requestAnimationFrame(tick);
      } else {
        setDisplayValue(targetValue);
        onComplete?.();
      }
    };

    setDisplayValue(0);
    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [duration, isDisabled, onComplete, targetValue]);

  return displayValue;
};

/**
 * Ease-out cubic time function.
 * Quick initial motion, then smooth deceleration into the final value.
 */
const easeOutCubic = (t: number): number => {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - (1 - clamped) ** 3;
};

/**
 * Format the animated step value.
 * Integers are rounded per step; decimals keep up to two fractional digits while moving.
 */
const formatAnimatedStep = (raw: number, target: number): number => {
  if (Number.isInteger(target)) {
    return Math.round(raw);
  }

  return Math.round(raw * 100) / 100;
};
